import { LendingModel } from './PriceTier';
import { LoanFormat } from './LoanFormat';
import { AgeCategory } from './AgeCategory';
import { LENDING_MODEL_OCOU, LENDING_MODEL_SIM, ProductClass } from '../js/store/constants';
import { FilterSetting, InvoiceOut, PreOrderStockDetails, ReviewOut, StockDetails } from '../js/store/api';
import { Article } from './Article';

export type UsageCondition =
    | {
          readonly usageLimit?: number;
          readonly termLimit?: number;
          readonly lendingModel?: LendingModel;
      }
    | {
          readonly usageLimit?: null;
          readonly termLimit?: null;
          readonly lendingModel?: null;
      };

// TODO: support different representations of products such as minimal-/short-/full-products
export type Author = {
    readonly id?: string; // e.g. '10355819@@Garfunkel, Art';
    readonly name?: string; // 'Garfunkel, Art';
    readonly firstName?: string; // 'Art';
    readonly lastName?: string; // 'Garfunkel';
};

export type Narrator = {
    readonly id?: string; // e.g. '10460178@@Garfunkel, Art';
    readonly name?: string; // e.g. 'Garfunkel, Art';
    readonly firstName?: string; // e.g. 'Art';
    readonly lastName?: string; // e.g. 'Garfunkel';
};

export type ProductFormatWithLayout = 'MP3' | 'PDF' | 'EPUB' | 'EPUB_FIXED_LAYOUT';

// TODO: mark optional/nullable product properties
export type ProductMetaData = {
    readonly id: string; // e.g. 'PUA_407074',
    readonly currentIssueId?: string;
    readonly accepted?: boolean;
    readonly availableForSale?: boolean;
    readonly reserveAmount?: number;
    readonly copyAmount?: number;
    readonly netValue?: number | undefined;
    readonly releaseDate?: number; // e.g. 1506384000000,
    readonly loanFormat: LoanFormat;
    readonly productTypes?: string[]; // e.g. ENEWS
    readonly title?: string; // e.g. 'What Is It All but Luminous',
    readonly subTitle?: string; // e.g. 'Notes From an Underground Man',
    readonly titleWithoutPrefix?; // e.g. string: 'What Is It All but Luminous',
    readonly summary?: string;
    readonly authors?: ReadonlyArray<Author>;
    readonly narrators?: ReadonlyArray<Narrator>;
    readonly isbn13?: string; // e.g. '9780525499923';
    readonly genres?: ReadonlyArray<GenreMetaData>;
    readonly coverUrl?: string; // e.g. 'https://covers.borrowbox.io/v3/covers/PUA_01069114?w=157';
    readonly publisher?: string; // e.g. 'Books on Tape';
    readonly publisherImprint?: string; // e.g. 'Books on Tape';
    readonly fiction?: boolean;
    readonly duration?: number; // e.g. 18540;
    readonly ageCategory?: AgeCategory;
    readonly ageGroup?: string; // e.g. '15+';
    readonly productionFormat?: string; // e.g. 'Unabridged';
    readonly productFormat?: ProductFormatWithLayout; // e.g. 'MP3', 'PDF', 'EPUB', 'EPUB_FIXED_LAYOUT';
    readonly explicit?: boolean;
    readonly pages?: number;
    readonly releaseQuantity?: number;
    readonly releaseFrequency?: string;
    readonly illustrated?: boolean;
    readonly supplementaries?: ReadonlyArray<{
        readonly type?: string; // e.g. 'Quote';
        readonly information?: string;
        readonly source?: string | null;
    }>;
    readonly sampleURL?: string;
    readonly sampleUrl?: string;
    readonly selections?: ReadonlyArray<string>;
    readonly awards?: ReadonlyArray<{
        readonly year?: number;
        readonly name?: string;
        readonly position?: string;
    }>;
    // TODO: sales status should be part of an Article, but this requires some API changes first
    readonly salesStatus?: {
        readonly key?: string; // e.g. 'PREORDER_EMBARGO';
        readonly value?: string; // e.g. 'Pre-order (Embargo)';
    };
    readonly productClass: ProductClass;
    readonly language?: string; // e.g. 'English';
    readonly badges?: {
        readonly warningBadges?: ReadonlyArray<{
            readonly title: string;
        }>;
        readonly premiumBadges?: ReadonlyArray<{
            readonly title: string;
        }>;
        readonly infoBadges?: ReadonlyArray<{
            readonly title: string;
        }>;
    };
    readonly campaignAvailable?: boolean;
    readonly usageCondition?: string; // e.g. 'No Loan or Term Limit';
    /**
     * @deprecated use {@link Article.price} instead
     */
    readonly simultaneousAccess?: boolean;
    /**
     * @deprecated use {@link Article.price} instead
     */
    readonly usageLimit?: number;
    /**
     * @deprecated use {@link Article.price} instead
     */
    readonly termLimit?: number;
    readonly campaignDetails?: {
        readonly standardProductId: string;
        readonly salesEndDate?: number;
    };
    readonly status?: ReadonlyArray<{
        amount?: number;
        invoice?: InvoiceOut;
        lendingModel?: LendingModel;
        status?: string;
        statusKey?: string;
        termLimit?: number;
        usageLimit?: number;
        preOrderShipDate: string; // e.g. "2016-11-03 00:00"
    }>;
    readonly seriesName?: string;
    readonly seriesNumber?: string;
    readonly copyStockAmount?: number;
    readonly campaignStockAmount?: number;

    readonly stockDetails?: StockDetails;
    readonly ownStockQuantity?: number;
    readonly foreignStockQuantity?: number;

    readonly preOrderStockDetails?: PreOrderStockDetails;

    readonly combinedReserveInfo?: {
        readonly foreignReserveCount?: number;
        readonly ownReserveCount?: number;
    };
    readonly teaser?: string;
    readonly relatedBundlesByAuthor?: {
        readonly [authorId: string]: {
            readonly id?: string;
            readonly bundleId?: string;
            readonly title?: string;
            readonly coverUrl?: string;
            readonly sidebarTopHit?: boolean;
        };
    };
    readonly relatedBundlesByProduct?: {
        readonly [productId: string]: {
            readonly id?: string;
            readonly bundleId?: string;
            readonly title?: string;
            readonly coverUrl?: string;
            readonly sidebarTopHit?: boolean;
        };
    };
    readonly copiesInCart?: number;
    readonly planStartDate?: string;
    readonly planEndDate?: string;
    readonly reviews?: Array<ReviewOut>;
};

export type GenreMetaData = {
    readonly id: string; // e.g. '1047@@Music';
    readonly name: string; // e.g. 'Music';
    readonly subGenres?: ReadonlyArray<SubGenreMetaData>;
};

export type SubGenreMetaData = {
    readonly id: string; // e.g. '1047@@Music->1256@@Pop';
    readonly name: string; // e.g. 'Pop';
};

/**
 * A line item is part of a product within a list.
 */
export type LineItem = ProductMetaData & Article;
export type LineItemOrProductId = LineItem | string;

/**
 * A product within the shop/catalogue.
 */
export type ShopProduct = ProductMetaData &
    Article & {
        readonly lineItems: undefined | null;
    };

/**
 * A product within a selection-list or cart.
 */
export type ListProduct = ProductMetaData & {
    /**
     * All line items of the product, each with a different usage condition.
     */
    readonly lineItems: ReadonlyArray<Article>;
};

export type Product = ShopProduct | ListProduct;

export type ProductFilter = {
    [key: string]: FilterSetting;
};

export enum ProductSorting {
    TITLE_AZ = 'titleAZ',
    AUTHOR_AZ = 'authorAZ',
    NARRATOR_AZ = 'narratorAZ',
    SERIES_AZ = 'seriesAZ',
    PUBLISHER_AZ = 'publisherAZ',
    STOCK = 'stock',
    RESERVE = 'reserve',
    RELEVANCE = 'relevance',
    RESERVES_PER_COPY = 'reservesPerCopy',
    CAMPAIGN_SALES_END_DATE_CLOSE_TO_FAR = 'campaignSalesEndDateCloseToFar',
    CAMPAIGN_SALES_END_DATE_FAR_TO_CLOSE = 'campaignSalesEndDateFarToClose',
    PRICE_HIGH_TO_LOW = 'priceHighToLow',
    PRICE_LOW_TO_HIGH = 'priceLowToHigh',
    DURATION_LONG_TO_SHORT = 'durationLongToShort',
    DURATION_SHORT_TO_LONG = 'durationShortToLong',
    PAGES_MORE_TO_LESS = 'pagesMoreToLess',
    PAGES_LESS_TO_MORE = 'pagesLessToMore',
    RELEASE_DATE_NEW_TO_OLD = 'releaseDateNewToOld',
    RELEASE_DATE_OLD_TO_NEW = 'releaseDateOldToNew',
    NEWLY_ADDED_DATE_NEW_TO_OLD = 'newlyAddedDateNewToOld',
    NEWLY_ADDED_DATE_OLD_TO_NEW = 'newlyAddedDateOldToNew'
}

export const isListProduct = (product: Product): product is ListProduct => {
    return Array.isArray(product.lineItems) && product.lineItems.length > 0;
};

export const mapProductFormatToLabel = (productFormat: ProductFormatWithLayout) => {
    switch (productFormat) {
        case 'EPUB':
            return 'epub';
        case 'EPUB_FIXED_LAYOUT':
            return 'fixedLayoutEpub';
        case 'PDF':
            return 'fixedLayoutPdf';
        case 'MP3':
            return 'mp3';
        default:
            return 'none';
    }
};

export const mapUsageConditionFromProductMetaData = (product: ProductMetaData): UsageCondition => {
    return {
        lendingModel: product.simultaneousAccess ? LENDING_MODEL_SIM : LENDING_MODEL_OCOU,
        usageLimit: product.usageLimit,
        termLimit: product.termLimit
    };
};
